import { CommonModule, DOCUMENT } from '@angular/common';
import { Component, DestroyRef, Inject, NgZone } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NotificationService } from '@shared-services/handlers/notification.service';
import { LoaderService } from 'app/core/services/loader.service';
import { BUSY_TIMING, showAfterDelay } from '@utils/busy-indicator';

export const LOADER_TIMEOUT_COPY = {
  generic: 'No pudimos confirmar la operación. Intentá de nuevo.',
  money: 'Puede haberse completado, revisá antes de reintentar.',
} as const;

/** Elements made inert while the overlay is up (app shell + open dialogs, not snackbars). */
const INERT_TARGETS_SELECTOR = '.gd-app-shell, .mat-mdc-dialog-container';

@Component({
  selector: 'gd-spinner',
  templateUrl: './spinner.component.html',
  styleUrls: ['./spinner.component.scss'],
  standalone: true,
  imports: [CommonModule],
})
export class SpinnerComponent {
  isVisible = false;
  readonly message$ = this.loaderService.message$;

  private inertElements: HTMLElement[] = [];
  private previouslyFocused: HTMLElement | null = null;
  /** Catches dialogs opened while the overlay is already up. */
  private dialogObserver: MutationObserver | null = null;

  constructor(
    private readonly loaderService: LoaderService,
    private readonly notificationService: NotificationService,
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly ngZone: NgZone,
    destroyRef: DestroyRef,
  ) {
    showAfterDelay(loaderService.isLoading$, BUSY_TIMING.overlay)
      .pipe(takeUntilDestroyed(destroyRef))
      .subscribe((visible) => this.setVisible(visible));

    loaderService.timedOut$
      .pipe(takeUntilDestroyed(destroyRef))
      .subscribe(({ money }) => {
        // The overlay closes (requests are NOT cancelled): release the page
        // first so the notice is not shown over inert content.
        this.setVisible(false);
        this.notificationService.showToastWarning(
          money ? LOADER_TIMEOUT_COPY.money : LOADER_TIMEOUT_COPY.generic,
          'La operación está tardando',
        );
      });

    destroyRef.onDestroy(() => this.releaseInert());
  }

  private setVisible(visible: boolean): void {
    if (visible === this.isVisible) {
      return;
    }
    this.isVisible = visible;

    if (visible) {
      const active = this.document.activeElement;
      this.previouslyFocused =
        active instanceof HTMLElement && active !== this.document.body
          ? active
          : null;
      this.applyInert();
      // Outside the zone: a callback inside it triggers change detection,
      // whose DOM updates fire the observer again and freeze the page.
      this.ngZone.runOutsideAngular(() => {
        this.dialogObserver = new MutationObserver((mutations) => {
          if (mutations.some((mutation) => mutation.addedNodes.length > 0)) {
            this.applyInert();
          }
        });
        this.dialogObserver.observe(this.document.body, {
          childList: true,
          subtree: true,
        });
      });
    } else {
      this.releaseInert();
    }
  }

  private applyInert(): void {
    this.document
      .querySelectorAll<HTMLElement>(INERT_TARGETS_SELECTOR)
      .forEach((element) => {
        if (!element.hasAttribute('inert')) {
          element.setAttribute('inert', '');
          this.inertElements.push(element);
        }
      });
  }

  private releaseInert(): void {
    this.dialogObserver?.disconnect();
    this.dialogObserver = null;
    this.inertElements.forEach((element) => element.removeAttribute('inert'));
    this.inertElements = [];

    const target = this.previouslyFocused;
    this.previouslyFocused = null;
    if (target?.isConnected) {
      target.focus({ preventScroll: true });
    }
  }
}
